import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  OnDestroy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MapBoxProvider } from 'leaflet-geosearch';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSwitchComponent } from 'ng-zorro-antd/switch';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';

import { MapComponent } from '../map';
import { NavButtonComponent } from '../../nav-button/nav-button';
import { UiService } from '../../../services/ui.service';
import { I18nService } from '../../../services/i18n.service';
import { AssetsService } from '../../../services/asset.service';
import { Sensor } from '../../../models/classes/sensor';
import { LP_UI_CONFIG } from '../../../core/tokens';
import { sanitizeUnicodeText } from '../../../utilities/global.util';
import { GLOBAL_CONSTANT } from '../../../constants/global.constant';
@Component({
  selector: 'app-sensor-map',
  templateUrl: './sensor-map.html',
  styleUrls: ['./sensor-map.scss'],
  imports: [
    MapComponent,
    NavButtonComponent,
    NzIconModule,
    NzSwitchComponent,
    NzAutocompleteModule,
    FormsModule,
  ],
})
export class SensorMapComponent implements OnInit, OnDestroy {
  private readonly cfg = inject(LP_UI_CONFIG);
  /** ===== DI via inject() ===== */
  private readonly ui = inject(UiService);
  private readonly router = inject(Router);
  public readonly i18n = inject(I18nService);
  private readonly assetsService = inject(AssetsService);

  /** ===== Signal Inputs / Outputs ===== */
  /** Initial location for the map (provided by parent) */
  StartLocation = input<{ lat: number; lng: number } | null>(null);
  /** Emits when the position changes */
  positionChanged = output<{ lat: number; lng: number }>();
  /** Emits when user wants to exit/close */
  exit = output<void>();

  /** ===== State (signals) ===== */
  showSensors = signal<boolean>(false);
  satelliteView = signal<boolean>(true);
  sensors = signal<Sensor[]>([]);

  searchString = signal<string>(GLOBAL_CONSTANT.EMPTY_STRING);
  searchResults = signal<
    {
      label: string;
      description: string;
      value: any;
      searchTerm?: string;
      fulltext?: string;
    }[]
  >([]);
  private searchProvider: MapBoxProvider | null = null;

  userLocation = signal<{ lat: number; lng: number } | null>(null);
  sensorPosition = signal<{ lat: number; lng: number } | null>(null);
  showSensorPosition = signal<boolean>(true);

  /** ===== Derived (computed) ===== */
  isMobile = computed(() => this.ui.IsMobile());

  /** Debounce timer for search effects */
  private searchDebounceTimer: any = null;
  /** One-time geolocation guard */
  private requestedGeoOnce = false;

  async ngOnInit() {
    await this.getMapKey();
    // If parent provided a StartLocation, reflect it in component state.
    effect(() => {
      const start = this.StartLocation();
      if (start) {
        this.sensorPosition.set(start);
        this.showSensorPosition.set(true);
      } else {
        // If no StartLocation and geolocation available, ask once.
        if (!this.requestedGeoOnce && navigator.geolocation) {
          this.requestedGeoOnce = true;
          navigator.geolocation.getCurrentPosition((position) => {
            const loc = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            this.userLocation.set(loc);
            // Optionally: set this as initial sensorPosition
            // this.sensorPosition.set(loc);
          });
        }
      }
    });

    // Initialize MapBox search provider (Promise-based).
    try {
      const keys = await this.assetsService.getMapBoxKeys(); // Ensure service returns a Promise
      this.searchProvider = new MapBoxProvider({
        params: {
          access_token: keys.geoCodingToken,
          language: this.i18n.languageSignal(), // user language
        },
      });
      //const keys = this.cfg.mapbox;
      // this.searchProvider = new MapBoxProvider({
      //   params: {
      //     access_token: keys.geoCodingToken,
      //     language: this.i18n.languageSignal(),
      //   },
      // });
    } catch (err) {
      console.error('Failed to initialize MapBoxProvider:', err);
    }

    // Debounced search using signals + effect (no RxJS Subject needed)

    effect(() => {
      const query = this.searchString();
      if (query === GLOBAL_CONSTANT.EMPTY_STRING) {
        this.showSensorPosition.set(true);
        this.searchResults.set([]);
        return;
      }
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(async () => {
        const provider = this.searchProvider;
        if (!provider || !query) {
          this.searchResults.set([]);
          return;
        }

        try {
          const res = await provider.search({ query });
          this.showSensorPosition.set(false);

          // Remove duplicates by label
          const unique: any[] = [...new Map(res.map((item: any) => [item.label, item])).values()];

          // Transform results (match original logic)
          const results = unique.map((r: any) => {
            const idx = r.label.indexOf(',');
            const label =
              idx > GLOBAL_CONSTANT.DEFAULT_INITIALIZER ? r.label.substr(0, idx) : r.label;
            const description = idx > 0 ? r.label.substr(idx + 1) : GLOBAL_CONSTANT.EMPTY_STRING;
            const labelLower = label.toLocaleLowerCase();
            const queryLower = query.toLocaleLowerCase();
            const matchIndex = labelLower.indexOf(queryLower);
            const shortenedLabel =
              matchIndex >= 0
                ? label.substring(0, matchIndex) + label.substring(matchIndex + query.length)
                : label;
            return {
              label: shortenedLabel,
              searchTerm:
                label.length > shortenedLabel.length ? query : GLOBAL_CONSTANT.EMPTY_STRING,
              description,
              value: r,
              fulltext: r.label,
            };
          });

          this.searchResults.set(results);
        } catch (err) {
          console.error('Search failed:', err);
          this.searchResults.set([]);
        }
      }, 300);
    });

    try {
      this.assetsService.getSensors().then((res) => {
        this.sensors.set(res ?? []);
      });
    } catch (err) {
      console.error('Failed to load sensors:', err);
      this.sensors.set([]);
    }
  }

  async getMapKey() {
    let keys: any = {};
    const res = await this.assetsService.getMapBoxKeys();
    keys = res;
    this.searchProvider = new MapBoxProvider({
      params: {
        access_token: keys.geoCodingToken,
        language: this.i18n.languageSignal(), // user language
      },
    });
  }

  /** ===== Lifecycle ===== */
  ngOnDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  }

  /** ===== Events from template ===== */

  /** When a position is changed on the embedded map (child component emits) */
  onPositionChanged(event: { lat: number; lng: number }) {
    // Reflect in local state if you wish:
    // this.sensorPosition.set(event);
    this.positionChanged.emit(event);
  }

  /** When user clicks Exit/Back */
  onExit() {
    this.exit.emit();
  }

  /** Search input focus/blur toggles pin visibility */
  onInputFocus(_event: FocusEvent) {
    this.showSensorPosition.set(false);
  }
  onInputBlur(_event: FocusEvent) {
    this.showSensorPosition.set(true);
  }

  /** Two-way binding helper for a text box: write side */
  onSearchChange(value: string) {
    this.searchString.set(value ?? GLOBAL_CONSTANT.EMPTY_STRING);
  }

  /** Autocomplete selection handler */
  onSearchSelect(selectedValue: { x: number; y: number }) {
    if (
      !selectedValue ||
      typeof selectedValue.x !== 'number' ||
      typeof selectedValue.y !== 'number'
    )
      return;
    const pos = { lat: selectedValue.y, lng: selectedValue.x };
    this.showSensorPosition.set(true);
    this.sensorPosition.set(pos);
    this.positionChanged.emit(pos);
  }

  onTextInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const cleaned = sanitizeUnicodeText(inputEl.value);

    if (cleaned !== inputEl.value) {
      inputEl.value = cleaned; // update the DOM value
    }
    //sync to model:
    this.onSearchChange(cleaned);
  }
}
